












































































import Debug from 'debug';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { resolve } from '@/app/container';
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';
import { CompetenceService, CompetenceServiceSymbol } from '@/app/competence/competence-service';
import { getPracticeQuizResultMessage } from '@/app/quiz/utils/practice-quiz-result-messages';
import UiProgressRingComponent from '@/app/shared/components/ui/ui-progress-ring-component.vue';
import UiStatusBarComponent from '@/app/shared/components/ui/ui-status-bar-component.vue';
import UiBadgeComponent from '@/app/shared/components/ui/ui-badge-component.vue';
import { examScoreDisplay, examPercentDisplay } from '@/app/exam/ui/exam-score-display'
import { practiceScoreDisplay, practiceLevelDisplay } from '@/app/practice/ui/practice-score-display'

const debug = Debug('quiz-result-component')

const PRACTICE_PERCENT_SHOW_EXAM_BUTTON: number = 0; // one percent is enough to show the exam button

const USE_PRACTICE_CUSTOM_FEEDBACK_MESSAGE = false; // do not use custom feedback messages for practice now

type Mode = 'practice' | 'exam'

interface ExamResult {
  passed: boolean
  score: {
    min: number
    max: number
    raw: number
    percent: number
  }
}

interface PracticeResult {
  level: { before: number, after: number }
}

type UiModel = {
  title: TranslateResult
  resultText: {
    title: TranslateResult
    message: string
  }
  circle: CircleDetails
  tryAgain: null | {
    title: TranslateResult
    message: TranslateResult
    buttonText: TranslateResult
  }
} & (ExamUiModel | PracticeUiModel | EmptyUiModel)

interface CircleDetails {
  currentValue: number
  fillValue: number
  color: 'red' | '',
  title: TranslateResult
  text: TranslateResult
  outerColor: ''
}

interface Feedback {
  success: string
  fail: string
}

interface ExamUiModel {
  type: 'exam'
  result: 'passed' | 'failed'
  showPracticeButton: boolean
}

interface PracticeUiModel {
  type: 'practice'
  showExamButton: boolean
}

interface EmptyUiModel {
  type: 'none'
}

function defaultUiModel(): UiModel {
  return {
    title: '',
    type: 'none',
    resultText: { title: '', message: '' },
    circle: { currentValue: 0, fillValue: 0, color: '', title: '', text: '', outerColor: '' },
    tryAgain: {
      title: '',
      message: '',
      buttonText: ''
    }
  }
}

@Component({
  components: {
    UiStatusBarComponent,
    UiProgressRingComponent,
    UiBadgeComponent
  }
})
export default class QuizResultComponentNew extends Vue {
  @Prop({ required: true})
  public mode: Mode;

  @Prop({ default: null})
  public examResult: ExamResult | null

  @Prop({ required: true })
  public feedback: Feedback;

  @Prop({ default: null})
  public practiceResult: PracticeResult | null

  @Prop({ required: true})
  public examAllowed: boolean;

  @Prop({ required: true})
  public practiceAllowed: boolean;

  private model: UiModel = defaultUiModel()

  private get competenceData(): any[] {
    let index: number = 1;
    let results: any[] = [];
    const competenceService: CompetenceService = resolve<CompetenceService>(CompetenceServiceSymbol);
    for (const [name, percent] of Object.entries(competenceService.getPercentsByCompetences())) {
      results = [...results, {
        id: index,
        title: name,
        percentage: percent,
        percentage2: percent
      }];

      index++;
    }

    return results;
  }

  private mounted(): void {

    this.model = this.createModel()

    $(() => {
      $(document).on('click', '.task-result .title', (e: any) => {
        $('.task-result').removeClass('open');
        $(e.target).parent('.task-result').addClass('open');
      });
    });
  }

  private createModel() {
    switch (this.mode) {
      case 'exam':
        if (!this.examResult) { return defaultUiModel() }
        return this.createExamModel(this.examResult)
      case 'practice':
        if (!this.practiceResult) { return defaultUiModel() }
        return this.createPracticeModel(this.practiceResult)
    }
  }

  private createPracticeModel(practiceResult: PracticeResult): UiModel {

    debug('createPracticeModel', practiceResult)

    if (!this.practiceResult) {
      return defaultUiModel()
    }

    const level = practiceResult.level
    const percent = practiceLevelDisplay(level.after)
    const diffPercent = percent - practiceLevelDisplay(level.before)
    const messageByLevel = getPracticeQuizResultMessage(percent)

    let customMessage = USE_PRACTICE_CUSTOM_FEEDBACK_MESSAGE
          ? this.practiceFeedbackCustomMessage(practiceResult)
          : ''
    
    const resultText = {
      title: this.t(messageByLevel.title).toString(),
      message: customMessage || this.t(messageByLevel.message).toString()
    }

    const circleTitle = diffPercent >= 0
            ? this.t('quiz.result.x_percent_correction', { diff: diffPercent })
            : this.t('quiz.result.x_percent_worse', { diff: diffPercent });

    const circle: CircleDetails = {
      currentValue: percent,
      fillValue: diffPercent,
      color: diffPercent < 0 ? 'red' : '',
      title: circleTitle,
      text: this.t('quiz.result.practice_level', { level: percent }),
      outerColor: ''
    }

    const result: UiModel = {
      type: 'practice',
      title: this.t(`quiz.result.practice_title`),
      resultText,
      circle,
      tryAgain: {
        title: this.t('quiz.result.new_trial'),
        message: this.t(`quiz.result.new_trial_message_practice`),
        buttonText: this.t('quiz.result.continue_practicing')
      },
      showExamButton: this.examAllowed && percent >= PRACTICE_PERCENT_SHOW_EXAM_BUTTON
    }

    debug('createPracticeModel result', result)
    return result
  }

  private practiceFeedbackCustomMessage(practiceResult: PracticeResult) {
    const isIncrease = practiceResult.level.after >= practiceResult.level.before
    if (isIncrease && this.feedback.success) {
      return this.feedback.success
    } else if (!isIncrease && this.feedback.fail) {
      return this.feedback.fail
    }

    return ''
  }

  private createExamModel(examResult: ExamResult): UiModel {

    debug('createExamModel', examResult)

    const percentText = examPercentDisplay(examResult.score.percent)

    let title
    let message
    if (examResult.passed) {
      title = this.t('quiz.result.success_title_exam')
      message = this.feedback.success ||
            this.t('quiz.result.success_message_exam', { percent: percentText } ).toString()
    } else {
      title = this.t('quiz.result.fail_title_exam')
      message = this.feedback.fail || this.t('quiz.result.fail_message_exam').toString()
    }
    
    const resultText = {
      title,
      message
    }

    const pointText: TranslateResult = this.t('quiz.point');
    const scoreDisplay = {
      raw: examScoreDisplay(examResult.score.raw),
      max: examScoreDisplay(examResult.score.max)
    }
    const circleTitle = `${scoreDisplay.raw} / ${scoreDisplay.max} ${pointText}`

    const circle: CircleDetails = {
      currentValue: examResult.score.percent,
      fillValue: examResult.score.percent,
      color: !examResult.passed ? 'red' : '',
      title: circleTitle,
      text: `${percentText}%`,
      outerColor: ''
    }

    let tryAgain
    if (examResult.passed) {
      if (examResult.score.percent >= 100) {
        // we do not show message if 100%
        tryAgain = null
      } else {
        tryAgain = {
          title: this.t('quiz.result.passed_exam_title'),
          message: this.t('quiz.result.passed_exam_message'),
          buttonText: this.t('quiz.result.new_record_attempt')
        }
      }
    } else {
      tryAgain = {
        title: this.t('quiz.result.new_trial'),
        message: this.practiceAllowed ?
              this.t(`quiz.result.new_trial_message_exam_has_practice`) :
              this.t(`quiz.result.new_trial_message_exam`),
        buttonText: this.t('quiz.result.try_again')
      }
    }

    const result: UiModel = {
      type: 'exam',
      title: this.t(`quiz.result.exam_title`),
      result: examResult.passed ? 'passed' : 'failed',
      resultText,
      circle,
      tryAgain,
      showPracticeButton: this.practiceAllowed && !examResult.passed
    }

    debug('createExamModel result', result)
    return result
  }

    private handleTryAgainClicked(): void {
      this.emitTryAgain(this.mode);
    }

    private handleStartExamClicked() {
      this.emitTryAgain('exam');
    }

    private handleStartPracticeClicked(): void {
      this.emitTryAgain('practice');
    }

    @Emit('try-again')
    private emitTryAgain(mode: Mode): Mode {
      debug('emitTryAgain', mode)
      return mode;
    }

    private t(key: string, params: any = {}): TranslateResult {
      return i18n.t(key, params);
    }
  }
